import { JobStatus } from '@/types';

/** Aligns with backend job status ids. */
export const JobStatusId = {
  CancelledBySystem: 1,
  Requested: 2,
  Scheduled: 3,
  Started: 4,
  OnHold: 5,
  Completed: 6,
  CancelledByUser: 7,
  Failed: 8,
} as const;

const TERMINAL_STATUS_IDS = new Set<number>([
  JobStatusId.CancelledBySystem,
  JobStatusId.CancelledByUser,
  JobStatusId.Completed,
  JobStatusId.Failed,
]);

/** Never offered as a user-selectable target in job / job-item status UI. */
const HIDDEN_FROM_STATUS_PICKER = new Set<number>([
  JobStatusId.CancelledBySystem,
]);

const ALLOWED_NEXT: Record<number, readonly number[]> = {
  [JobStatusId.Requested]: [
    JobStatusId.Scheduled,
    JobStatusId.OnHold,
    JobStatusId.CancelledByUser,
  ],
  [JobStatusId.Scheduled]: [
    JobStatusId.Started,
    JobStatusId.OnHold,
    JobStatusId.CancelledByUser,
  ],
  [JobStatusId.Started]: [
    JobStatusId.Completed,
    JobStatusId.Failed,
    JobStatusId.CancelledByUser,
  ],
  [JobStatusId.OnHold]: [JobStatusId.Scheduled, JobStatusId.CancelledByUser],
};

function statusMetaById(allStatuses: JobStatus[] | undefined) {
  return new Map((allStatuses || []).map((s) => [s.jobStatusId, s]));
}

export function isTerminalJobStatus(jobStatusId: number): boolean {
  return TERMINAL_STATUS_IDS.has(jobStatusId);
}

export function formatJobStatusOptionLabel(
  statusId: number,
  allStatuses?: JobStatus[],
): string {
  if (statusId === JobStatusId.CancelledByUser) {
    return 'Cancelled';
  }
  const meta = statusMetaById(allStatuses).get(statusId);
  return meta?.description || meta?.name || String(statusId);
}

/** Display label for table cells (uses row text when API list has no match). */
export function getJobStatusDisplayLabel(
  statusId: number,
  rowStatusText?: string,
  allStatuses?: JobStatus[],
): string {
  if (statusId === JobStatusId.CancelledByUser) {
    return 'Cancelled';
  }
  const meta = statusMetaById(allStatuses).get(statusId);
  if (meta) {
    return meta.description || meta.name;
  }
  return rowStatusText || String(statusId);
}

export function canTransitionJobStatus(fromId: number, toId: number): boolean {
  if (fromId === toId) return true;
  if (isTerminalJobStatus(fromId)) return false;
  const allowed = ALLOWED_NEXT[fromId];
  if (!allowed) return false;
  return allowed.includes(toId);
}

export type JobStatusSelectOption = { value: number; label: string };

/**
 * Options for Select: current value, allowed next states, and optional optimistic value.
 * Omits CancelledBySystem from selectable lists.
 */
export function getJobStatusSelectOptions(
  serverStatusId: number,
  allStatuses: JobStatus[] | undefined,
  options?: { pendingStatusId?: number },
): JobStatusSelectOption[] {
  const byId = statusMetaById(allStatuses);

  if (serverStatusId === JobStatusId.CancelledBySystem) {
    return [
      {
        value: serverStatusId,
        label:
          byId.get(serverStatusId)?.description ||
          byId.get(serverStatusId)?.name ||
          'Cancelled (system)',
      },
    ];
  }

  const idSet = new Set<number>();

  if (isTerminalJobStatus(serverStatusId)) {
    idSet.add(serverStatusId);
  } else {
    idSet.add(serverStatusId);
    const next = ALLOWED_NEXT[serverStatusId];
    if (next) {
      next.forEach((id) => idSet.add(id));
    }
  }

  const pending = options?.pendingStatusId;
  if (pending != null) {
    idSet.add(pending);
  }

  return Array.from(idSet)
    .filter((id) => !HIDDEN_FROM_STATUS_PICKER.has(id))
    .map((id) => ({
      value: id,
      label: formatJobStatusOptionLabel(id, allStatuses),
    }))
    .sort((a, b) => a.value - b.value);
}
